import React from 'react'
import { SubscriptionContext } from '../../context'
import { R } from "@utils/currency"
import { Button } from '@components/Button'
import { Stack } from "@UILibrary/Stack"
import { Modal, ModalContent, ModalFooter, ModalHeader, ModalSubHeader } from '@components/Modal'
import useModalVisibilityControls from '@hooks/useModalVisibilityControls'

const baseClass = "billing-details"
const Show = () => {
  const { 
    state: { billable, invoice, permissions }
  } = React.useContext(SubscriptionContext)
  
  const formatAddress = (address) => (
    !!address ? `${address.line_1}, ${address.city}, ${address.code}, ${address.country}` : ''
  )
  
  if (!permissions.showBilling) return null
  
  return (
    <div className={`${baseClass}__breakdown-container`}>
      <div>
        <p>Subscription billing allows you to load a card that will be automatically invoiced and charged every month.</p>
        <p>Please ensure you enter the correct company billing information as you expect it to show on an invoice.</p>
        <p><strong>Current pricing plan:</strong></p>
        <ul>
          <li>Plan type: {invoice.plan_type}</li>
          <li>Advisor count: {invoice.advisor_count}</li>
          <li>Client count: {invoice.client_count} {invoice.additional_clients > 0 && `(${invoice.additional_clients} addi.)`}</li>
          {invoice.discount_percentage > 0 && <li>Discount: {invoice.discount_percentage}%</li>}
        </ul>
        <p>Estimated price: <strong>{R(invoice.total_inclusive).format()}</strong></p>
        <small>This is an estimated value based on your current plan. For any queries please contact <a href="mailto: support@wealthbit.co">support@wealthbit.co</a></small>
      </div>
      <div className={`${baseClass}__divider`} />
      <div className={`${baseClass}__billable-container`}>
        <div>
          <h4>Company details</h4>
          {billable?.id ? (
            <>
              <p>Contact name: {billable.contact_name}</p>
              <p>Email: {billable.contact_email}</p>
              <p>Company VAT: {billable.company_vat}</p>
              <p>Billing address: {formatAddress(billable.address)}</p>
            </>
          ) : (
            <p>Configure your billing details to subscribe to automated billing.</p>
          )}
        </div>
        {permissions.showSubscription && (
          <div>
            <h4>Card details</h4>
            {billable?.registration_id ? (
              <>
                <p>Card registered</p>
                <i>{billable.holder} {`${billable.expiry_month}/${billable.expiry_year}`}</i>
                <p>xxxx xxxx xxxx {billable.last_four_digits}</p>
              </>
            ) : (
              <p>Register a card to enable automated billing.</p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

type RemoveCardModalProps = {
  open: boolean,
  onClose: () => void,
  onConfirm: () => void
}

const RemoveCardModal = ({
  open,
  onClose,
  onConfirm,
}: RemoveCardModalProps) => {
  return (
    <Modal open={open} onClose={onClose}>
      <ModalHeader>In a hurry?</ModalHeader>
      <ModalSubHeader>You are about to remove your card, and unsubscribe from automated billing.</ModalSubHeader>
      <ModalContent>
        <p style={{ textAlign: "center" }}>Are you sure you would like to continue?</p>
        <Stack horizontal spacing="s" justifyContent="center">
          <Button secondary onClick={onClose}>Cancel</Button>
          <Button warning onClick={onConfirm}>Remove</Button>
        </Stack>
      </ModalContent>
    </Modal>
  )
}

const ShowActions = () => {
  const { 
    state: { billable, permissions },
    actions: { initiateCheckout, navigateToDetails, removeCard }
  } = React.useContext(SubscriptionContext)
 
  const { modalVisible, openModal, closeModal} = useModalVisibilityControls()

  const [loading, setLoading] = React.useState(false)

  const handleCardRegistration = async () => {
    setLoading(true)

    const success = await initiateCheckout();

    if (!success) {
      setLoading(false)
    }
  }

  const handleBillingDetails = () => {
    navigateToDetails()
  }
  
  const handleRemoveCard = async () => {
    const success = await removeCard()
    
    if (!!success) {
      closeModal()
    } 
  }
  
  if (!permissions.editBilling && !permissions.editSubscription) return null
  
  const isBillingSetup = !!billable?.id
  const isCardRegistered = !!billable?.registration_id

  return (
    <Stack horizontal spacing="s">
      {permissions.editSubscription && (
        !isCardRegistered ? (
          <Button secondary 
            disabled={!isBillingSetup}
            onClick={handleCardRegistration}>
            {loading ? "Preparing..." : "Register Card"}
          </Button>
        ) : (
          <Button warning 
            onClick={openModal}>
            Remove Card
          </Button>
        )
      )}
      {permissions.editBilling && (
        <Button primary
          onClick={handleBillingDetails}>
            {isBillingSetup ? "Update Billing Details" : "Add Billing Details"}
        </Button>
      )}
      <RemoveCardModal open={modalVisible} onClose={closeModal} onConfirm={handleRemoveCard} />
    </Stack>
  )
}

export { Show, ShowActions }